import React from 'react';
import { Tooltip } from 'antd';
import { useDispatch } from 'umi';
import classNames from 'classnames';

import { LeadTaskModel } from '@/typings/models/LeadTask';
import { AssigneeModel } from '@/typings/models/Assignee';
import { AgentActionTag } from '@/components/ActionTag';
import { pressableProps } from '@/utils/dom';
import { getTaskAssignees } from '@/utils/task';

import styles from './TaskAssignees.less';

type ITaskAssigneesProps = {
  task: LeadTaskModel;
  size?: 'small' | 'default';
  maxCount?: number;
};

// Deterministic soft palette (from the Aufgabenliste design's avatar colors)
const PALETTE = [
  { bg: '#e8f6f4', fg: '#1f8a70' },
  { bg: '#e9ebef', fg: '#5c6169' },
  { bg: '#eaf1fb', fg: '#3a6ea8' },
  { bg: '#fff0e7', fg: '#c9541a' },
  { bg: '#f3ecfa', fg: '#7a4fa8' },
];

const assigneeName = (assignee: AssigneeModel) =>
  (assignee as any).fullname || `${assignee.firstname || ''} ${assignee.lastname || ''}`.trim();

const assigneeInitials = (assignee: AssigneeModel) =>
  assigneeName(assignee)
    .split(' ')
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

const paletteFor = (assignee: AssigneeModel) => {
  const seed = String(assignee.id || assigneeName(assignee));
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  return PALETTE[Math.abs(hash) % PALETTE.length];
};

/**
 * A task's assignees as an overlapping stack of initials — the name appears on
 * hover, a click opens the agent's drawer.
 */
const TaskAssignees: React.FC<ITaskAssigneesProps> = ({ task, size = 'small', maxCount = 4 }) => {
  const dispatch = useDispatch();
  const assignees = getTaskAssignees(task);

  if (assignees.length === 0) return null;

  const openAgentDrawer = (agent: AssigneeModel) => {
    dispatch({
      type: 'user/toggleDetailsDrawer',
      payload: {
        agentId: agent.id,
        agent,
      },
    });
  };

  // A single assignee reads as the standard full agent tag (avatar + name);
  // the compact initials stack is for the multi-assignee case only.
  if (assignees.length === 1) {
    return (
      <span className={styles.singleTag} onClick={(e) => e.stopPropagation()}>
        <AgentActionTag agent={assignees[0] as any} size="medium" />
      </span>
    );
  }

  const visible = assignees.slice(0, maxCount);
  const overflow = assignees.length - visible.length;

  return (
    <span
      className={classNames(styles.stack, { [styles.stackSmall]: size === 'small' })}
      onClick={(e) => e.stopPropagation()}
    >
      {visible.map((assignee) => {
        const colors = paletteFor(assignee);
        return (
          <Tooltip key={assignee.id} title={assigneeName(assignee)}>
            <span
              className={styles.avatar}
              style={{ background: colors.bg, color: colors.fg }}
              onClick={() => openAgentDrawer(assignee)}
              aria-label={assigneeName(assignee)}
              {...pressableProps(() => openAgentDrawer(assignee))}
            >
              {assigneeInitials(assignee)}
            </span>
          </Tooltip>
        );
      })}
      {overflow > 0 && (
        <Tooltip title={assignees.slice(maxCount).map(assigneeName).join(', ')}>
          <span className={classNames(styles.avatar, styles.avatarOverflow)}>+{overflow}</span>
        </Tooltip>
      )}
    </span>
  );
};

export default React.memo(TaskAssignees);
